/**
 * lib/partnerApi.js
 * Shared helpers for the Supplier Management and Dealer Management modules
 * (backend/partners/). Both modules use the same pages, parameterised by
 * `kind` ("supplier" | "dealer").
 */
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import api, { formatApiError } from "@/lib/api";

export const KIND = {
  supplier: { label: "Supplier", plural: "Suppliers", base: "/supplier-master", api: "/supplier-master", eyebrow: "Procurement" },
  dealer: { label: "Dealer", plural: "Dealers", base: "/dealer-master", api: "/dealer-master", eyebrow: "Sales" },
};

export function titleCase(v) {
  return String(v || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function fmtMoney(v, currency = "INR") {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("en-IN", { style: "currency", currency, maximumFractionDigits: 2 });
}

export function fmtNum(v, digits = 2) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: digits });
}

export function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export const STATUS_TONE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  inactive: "bg-slate-100 text-slate-600 border-slate-200",
  blocked: "bg-red-50 text-red-700 border-red-200",
  under_review: "bg-amber-50 text-amber-700 border-amber-200",
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  expired: "bg-red-50 text-red-700 border-red-200",
  expiring: "bg-amber-50 text-amber-700 border-amber-200",
  valid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  open: "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
  posted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

/** Backend validation errors come back as {message, errors:[{field,message}]}. */
export function apiError(e, fallback = "Something went wrong") {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && !Array.isArray(d) && d.message) return d.message;
  return formatApiError(d) || fallback;
}

export function fieldErrors(e) {
  const d = e?.response?.data?.detail;
  const out = {};
  if (d && Array.isArray(d.errors)) d.errors.forEach((x) => { out[x.field] = x.message; });
  return out;
}

export async function fetchMeta(kind) {
  return (await api.get(`${KIND[kind].api}/meta`)).data;
}

// ---------------------------------------------------------------- export
function cellValue(row, col) {
  const v = row[col.key];
  if (v === null || v === undefined) return "";
  if (col.type === "date") return fmtDate(v) === "—" ? "" : fmtDate(v);
  if (col.type === "money" || col.type === "number") return typeof v === "number" ? v : Number(v) || v;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v).replace(/_/g, " ");
}

function stamp() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}

/** Log the export server-side (permission check + audit), then build the file. */
async function gate(kind, what, format, rows) {
  await api.post(`${KIND[kind].api}/export-log`, { what, format, rows });
}

export async function exportExcel(kind, title, columns, rows) {
  await gate(kind, title, "xlsx", rows.length);
  const data = [columns.map((c) => c.label), ...rows.map((r) => columns.map((c) => cellValue(r, c)))];
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws["!cols"] = columns.map((c) => ({ wch: Math.min(40, Math.max(10, c.label.length + 2)) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31).replace(/[\\/?*[\]:]/g, " "));
  XLSX.writeFile(wb, `${title.replace(/\s+/g, "_")}_${stamp()}.xlsx`);
}

export async function exportPdf(kind, title, columns, rows, subtitle = "") {
  await gate(kind, title, "pdf", rows.length);
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: columns.length > 7 ? "landscape" : "portrait" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 28;
  let y = M;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(title, M, y);
  y += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100);
  doc.text(`${subtitle ? subtitle + " · " : ""}Generated ${new Date().toLocaleString("en-IN")} · ${rows.length} row(s)`, M, y);
  doc.setTextColor(0);
  y += 14;
  const colW = (W - 2 * M) / columns.length;
  const fs = columns.length > 12 ? 6 : 7;
  const header = () => {
    doc.setFillColor(15, 23, 42);
    doc.rect(M, y - 9, W - 2 * M, 14, "F");
    doc.setTextColor(255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(fs);
    columns.forEach((c, i) => doc.text(doc.splitTextToSize(c.label, colW - 4)[0], M + i * colW + 2, y));
    doc.setTextColor(0);
    doc.setFont("helvetica", "normal");
    y += 12;
  };
  header();
  rows.forEach((r, idx) => {
    const cells = columns.map((c) => {
      const v = cellValue(r, c);
      const s = typeof v === "number" ? v.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : String(v);
      return doc.splitTextToSize(s, colW - 4).slice(0, 3);
    });
    const h = Math.max(...cells.map((c) => c.length)) * (fs + 2) + 3;
    if (y + h > H - M) {
      doc.addPage();
      y = M + 10;
      header();
    }
    if (idx % 2) {
      doc.setFillColor(248, 250, 252);
      doc.rect(M, y - fs, W - 2 * M, h, "F");
    }
    doc.setFontSize(fs);
    cells.forEach((lines, i) => doc.text(lines, M + i * colW + 2, y));
    y += h;
  });
  doc.save(`${title.replace(/\s+/g, "_")}_${stamp()}.pdf`);
}
